import { useCallback } from 'react';

import { Spinner } from '@/components/atoms/Spinner';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import { useCageTimezone } from '@/contexts/cage';
import useCageFilter from '@/contexts/cage-filter-context';
import { usePenCohortFishGroup } from '@/contexts/pen-cohort-context';
import { useGetCageWelfareOverview } from '@/domain/cage-welfare-overview';
import { fullSizeImageMapper, useGetWelfareImages } from '@/domain/images';
import { useGetFullWelfare } from '@/domain/welfare/useGetFullWelfare';
import { useImageState } from '@/hooks/image-state';
import CageWelfareDashboardPageContent from '@/routes/Client/Cage/pages/CageWelfare/CageWelfareDashboard/CageWelfareDashboardPageContent';
import type { CagePageInterface } from '@/routes/Client/Cage/utils/cage-pages-interface';
import { MERGED_WELFARE_IMAGES_TYPE } from '@/utils/welfareImageHelpers';

export default function CageWelfareDashboardContainer({
    clientId,
    locationId,
    cageId,
}: CagePageInterface) {
    const timezone = useCageTimezone();

    const { daypartTag, selectedFishGroupId } = useCageFilter();

    const welfareOverviewQuery = useGetCageWelfareOverview(
        cageId,
        { fishGroupId: selectedFishGroupId },
        {
            staleTime: 5 * 60 * 1000,
            gcTime: 5 * 60 * 1000,
        }
    );
    const {
        isLoading: cageWelfareOverviewIsLoading,
        data: cageWelfareOverview,
        error: cageWelfareOverviewError,
    } = welfareOverviewQuery;

    const imagesFishGroup = usePenCohortFishGroup();
    const detections = useGetWelfareImages(
        cageId,
        {
            type: MERGED_WELFARE_IMAGES_TYPE,
            numberOfImages: '1',
        },
        { fishGroup: imagesFishGroup }
    );
    const detectionImageState = useImageState({
        loading: detections.isLoading && !detections.error,
        data: detections.data,
        mapper: useCallback(fullSizeImageMapper(cageId), [cageId]),
        queryParam: 'imageId',
        timezone,
    });

    const { data: fishHealth, isLoading: fishHealthLoading } = useGetFullWelfare(
        cageId,
        {
            daypartTag: daypartTag ?? 'day',
            fishGroupId: selectedFishGroupId,
        },
        {
            staleTime: 5 * 60 * 1000,
        }
    );

    const loading = cageWelfareOverviewIsLoading || fishHealthLoading;

    if (loading) {
        return <Spinner size="lg" fullScreen />;
    }
    // The page's title goes with the content, so the error state stands in as its h1.
    if (cageWelfareOverviewError) {
        return (
            <SomethingHappened headingLevel="h1" {...retryFailedQueries(welfareOverviewQuery)} />
        );
    }

    return (
        <CageWelfareDashboardPageContent
            clientId={clientId}
            locationId={locationId}
            cageId={cageId}
            images={detectionImageState.loading ? [] : detectionImageState.images}
            cageWelfareOverview={cageWelfareOverview?.welfare}
            cageSpeed={cageWelfareOverview?.swimSpeed?.swimSpeed}
            detectionsLoading={detectionImageState.loading}
            fishHealthData={fishHealth.welfareData}
        />
    );
}
